import React from "react";
import {
	addTimeToDate,
	Column,
	Conditional,
	Else,
	If,
	isAfter,
	Row,
	stringToLocalDate,
	toLocalDateString,
} from "@hex-insights/core";
import { Tile } from "../../../../../Components/Tile";
import { Timeline } from "../../../../../Components/Timeline";
import {
	AcademicTerm,
	ClassYear,
	ClassYearDetailQuery,
	Student,
	StudentNameDescriptionAndImageFragment,
	TimelineFilterInput,
	timelineObjectStudentNoteTypesWithoutAttendance,
	TimelineObjectType,
	useHomeRoomSectionListQuery,
} from "../../../../../Utilities";
import { AcademicTermUtilities } from "../../../../Utilities";
import * as CourseSectionComponents from "../../../CourseSection";
import * as EmployeeComponents from "../../../Employee";
import * as HomeRoomSectionComponents from "../../../HomeRoomSection";
import * as SchoolAttendanceRecordComponents from "../../../SchoolAttendanceRecord";
import * as StudentComponents from "../../../Student";

export type BodyProps = {
	classYear: ClassYearDetailQuery["classYear"];
	academicTerm: Pick<AcademicTerm, "id" | "name" | "startDate" | "endDate"> & {
		academicYear: Pick<AcademicTerm["academicYear"], "id" | "name">;
	};
	isLoadingStudents: boolean;
	students: (Pick<Student, "id"> & StudentNameDescriptionAndImageFragment)[] | null | undefined;
};

export function Body({ classYear, academicTerm, isLoadingStudents, students }: BodyProps) {
	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column
				justify="spaced-start"
				style={{
					width: "25rem",
					flexShrink: 0,
					position: "sticky",
					top: "calc(var(--header---height) + 0.75rem)",
					height: "fit-content",
				}}
			>
				<AttendanceSection classYear={classYear} academicTerm={academicTerm} />
				<StudentsSection
					isLoading={isLoadingStudents}
					students={students}
					classYear={classYear}
					academicTerm={academicTerm}
				/>
				<TeachersSection classYear={classYear} academicTerm={academicTerm} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<HomeRoomSectionsSection classYear={classYear} academicTerm={academicTerm} />
				<CourseSectionsSection classYear={classYear} academicTerm={academicTerm} />
				<TimelineSection students={students} classYear={classYear} academicTerm={academicTerm} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	classYear: Pick<ClassYear, "id" | "name" | "yearsFromGraduation" | "graduationYear">;
	academicTerm: Pick<AcademicTerm, "id" | "name" | "startDate" | "endDate"> & {
		academicYear: Pick<AcademicTerm["academicYear"], "id" | "name">;
	};
};

function AttendanceSection({ academicTerm, classYear }: PageSectionProps) {
	const studentFilters = React.useMemo(
		() => ({
			graduationYearEQ: classYear.graduationYear,
			studentAcademicTermEnrollments: [
				AcademicTermUtilities.getCurrentlyEnrolledInAcademicTermFilterInput(academicTerm.id),
			],
		}),
		[academicTerm.id, classYear.graduationYear],
	);

	return <SchoolAttendanceRecordComponents.SchoolAttendanceRecordReportTile studentFilters={studentFilters} />;
}

type StudentsSectionProps = PageSectionProps & {
	isLoading: boolean;
	students: (Pick<Student, "id"> & StudentNameDescriptionAndImageFragment)[] | null | undefined;
};

function StudentsSection({ isLoading, students }: StudentsSectionProps) {
	// TODO indication for left/leaving early and arrived/arriving late
	return <StudentComponents.StudentsGridTile isLoading={isLoading} students={students} columns={8} rows={2} />;
}

function TeachersSection({ classYear, academicTerm }: PageSectionProps) {
	const filters = React.useMemo(
		() => ({
			or: [
				{
					teacherHomeRoomSectionEnrollments: [
						{
							homeRoomSection: [
								{
									academicTerm: [{ academicYearIDEQ: academicTerm.academicYear.id }],
									homeRoom: [{ classYear: [{ idEQ: classYear.id }] }],
								},
							],
						},
					],
				},
				{
					teacherCourseSectionEnrollments: [
						{
							courseSection: [
								{
									academicTerm: [{ academicYearIDEQ: academicTerm.academicYear.id }],
									course: [{ classYears: [{ idEQ: classYear.id }] }],
								},
							],
						},
					],
				},
			],
		}),
		[academicTerm.academicYear.id, classYear.id],
	);

	// TODO indication for left/leaving early and arrived/arriving late

	return <EmployeeComponents.ControlledEmployeesGridTile heading="Teachers" filters={filters} columns={8} rows={2} />;
}

function HomeRoomSectionsSection({ classYear, academicTerm }: PageSectionProps) {
	// TODO indication for term

	const { loading, data } = useHomeRoomSectionListQuery({
		variables: {
			filters: {
				homeRoom: [{ classYearIDEQ: classYear.id }],
				academicTerm: [{ academicYearIDEQ: academicTerm.academicYear.id }],
			},
		},
	});
	const homeRoomSections = React.useMemo(() => data?.homeRoomSectionConnection.edges.map((e) => e.node), [data]);
	const numHomeRoomSections = homeRoomSections?.length ?? 0;

	const columns = numHomeRoomSections === 1 ? 1 : 2;
	const rows = 1;

	return (
		<HomeRoomSectionComponents.HomeRoomSectionsPaginatedGridTile
			isLoading={loading}
			homeRoomSections={homeRoomSections}
			columns={columns}
			rows={rows}
		/>
	);
}

function CourseSectionsSection({ classYear, academicTerm }: PageSectionProps) {
	// TODO indication for term

	const filters = React.useMemo(
		() => ({
			course: [{ classYears: [{ idEQ: classYear.id }] }],
			academicTerm: [{ academicYearIDEQ: academicTerm.academicYear.id }],
		}),
		[classYear.id, academicTerm.academicYear.id],
	);

	return <CourseSectionComponents.ControlledCourseSectionsGridTile filters={filters} columns={2} rows={3} slim />;
}

type TimelineSectionProps = PageSectionProps & {
	students: Pick<Student, "id">[] | null | undefined;
};

function TimelineSection({ classYear, students, academicTerm }: TimelineSectionProps) {
	const filter = React.useMemo<TimelineFilterInput>(() => {
		const hasEnded = isAfter(new Date(), stringToLocalDate(academicTerm.endDate, "day"), "day");

		return {
			objectTypeIn: [
				TimelineObjectType.AcademicTerm,
				TimelineObjectType.Announcement,
				TimelineObjectType.CalendarEvent,
				TimelineObjectType.Newsletter,
				TimelineObjectType.Post,
				TimelineObjectType.StudentNote,
				...timelineObjectStudentNoteTypesWithoutAttendance,
			],
			startTime: toLocalDateString(addTimeToDate(stringToLocalDate(academicTerm.startDate, "day"), [-1, "day"])),
			endTime: hasEnded
				? toLocalDateString(addTimeToDate(stringToLocalDate(academicTerm.startDate, "day"), [1, "day"]))
				: undefined,
			classYearID: classYear.id,
		};
	}, [academicTerm, classYear.id]);

	return (
		<Tile style={{ flexGrow: 1 }}>
			<Tile.Body>
				<Conditional>
					<If condition={!students || students.length === 0}>
						<Column style={{ height: "100%" }}>
							<Timeline.Empty />
						</Column>
					</If>
					<Else>
						<Timeline filter={filter} pageSize={10} style={{ height: "100%" }} />
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}
